import React from 'react';
import SEO from '../components/SEO';

function AboutTheAuthor() {
  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title="About the Author | Giovanni Spadafore"
        description="Learn more about Giovanni Spadafore, owner and author at Copper Canyon Pressure Wash. Discover his experience, expertise, and commitment to quality service in Phoenix and beyond."
        author="Giovanni Spadafore"
        keywords="Giovanni Spadafore, about the author, Copper Canyon Pressure Wash, Phoenix, pressure washing, business owner, expert, blog author"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Person',
          'name': 'Giovanni Spadafore',
          'url': 'https://coppercanyonpressurewash.com/about-the-author',
          'image': 'https://coppercanyonpressurewash.com/giovanni-spadafore.jpg',
          'description': 'Founder and owner of Copper Canyon Pressure Wash, serving the Phoenix metropolitan area with a passion for quality, reliability, and customer satisfaction.',
          'affiliation': {
            '@type': 'Organization',
            'name': 'Copper Canyon Pressure Wash',
            'url': 'https://coppercanyonpressurewash.com'
          }
        }}
      />
      <div className="flex flex-col items-center text-center mb-8">
        <picture>
          <source srcSet="/giovanni-spadafore.avif" type="image/avif" />
          <source srcSet="/giovanni-spadafore.webp" type="image/webp" />
        <img 
          src="/giovanni-spadafore.jpg" 
          alt="Giovanni Spadafore, owner of Copper Canyon Pressure Wash" 
          className="w-40 h-40 rounded-full object-cover mb-4 border-4 border-[#b87c4c]"
          onError={e => { e.target.style.display = 'none'; }}
            width="160"
            height="160"
            loading="lazy"
          />
        </picture>
        <picture>
          <source srcSet="/Hero_Image.avif" type="image/avif" />
          <source srcSet="/Hero_Image.webp" type="image/webp" />
          <img 
            src="/Hero_Image.JPG" 
            alt="Copper Canyon Pressure Wash team at work" 
            title="Copper Canyon Pressure Wash – Professional Team at Work"
            className="w-full max-w-xl h-56 object-cover rounded-lg mb-4 border border-[#b87c4c]"
            style={{ objectPosition: '0 30%' }}
            width="800"
            height="224"
            loading="lazy"
        />
        </picture>
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-2">Giovanni Spadafore</h1>
        <h2 className="text-xl text-[#b87c4c] font-semibold mb-4">Owner & Author, Copper Canyon Pressure Wash</h2>
      </div>
      <div className="text-lg text-gray-700 mb-8">
        <p className="mb-4">
          Giovanni Spadafore is the founder and owner of Copper Canyon Pressure Wash, serving the Phoenix metropolitan area with a passion for quality, reliability, and customer satisfaction. With years of hands-on experience in the pressure washing industry, Giovanni is dedicated to helping homeowners and businesses maintain beautiful, clean, and safe properties.
        </p>
        <p className="mb-4">
          As the primary author of the Copper Canyon Pressure Wash blog, Giovanni shares expert tips, industry insights, and practical advice to help readers get the most out of their exterior cleaning projects. His commitment to eco-friendly solutions and top-notch service has earned Copper Canyon a reputation for excellence throughout the Valley.
        </p>
        <p className="mb-4">
          When he's not working or writing, Giovanni enjoys spending time outdoors, exploring Arizona, and giving back to the local community.
        </p>
      </div>
      <div className="flex flex-col items-center gap-2">
        <a href="mailto:coppercanyonpressurewash@gmail.com" className="text-[#b87c4c] hover:underline text-lg">Contact Giovanni</a>
        <div className="flex gap-4 mt-2">
          <a href="https://www.facebook.com/people/copper-canyon-pressure-wash/61570012767538" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <svg className="w-6 h-6 text-[#4a3f35] hover:text-[#b87c4c]" fill="currentColor" viewBox="0 0 24 24"><path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0"/></svg>
          </a>
          <a href="https://www.instagram.com/copper.canyon.p.w/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <svg className="w-6 h-6 text-[#4a3f35] hover:text-[#b87c4c]" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.974.974 1.246 2.241 1.308 3.608.058 1.266.069 1.646.069 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.974.974-2.241 1.246-3.608 1.308-1.266.058-1.646.069-4.85.069s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.974-.974-1.246-2.241-1.308-3.608C2.175 15.647 2.163 15.267 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608C4.515 2.497 5.782 2.225 7.148 2.163 8.414 2.105 8.794 2.163 12 2.163zm0-2.163C8.741 0 8.332.012 7.052.07 5.771.128 4.659.334 3.678 1.315 2.697 2.296 2.491 3.408 2.433 4.689 2.375 5.969 2.363 6.379 2.363 12c0 5.621.012 6.031.07 7.311.058 1.281.264 2.393 1.245 3.374.981.981 2.093 1.187 3.374 1.245 1.28.058 1.689.07 7.311.07s6.031-.012 7.311-.07c1.281-.058 2.393-.264 3.374-1.245.981-.981 1.187-2.093 1.245-3.374.058-1.28.07-1.689.07-7.311s-.012-6.031-.07-7.311c-.058-1.281-.264-2.393-1.245-3.374C19.485.334 18.373.128 17.093.07 15.813.012 15.404 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a3.999 3.999 0 1 1 0-7.998 3.999 3.999 0 0 1 0 7.998zm6.406-11.845a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z"/></svg>
          </a>
        </div>
      </div>
    </main>
  );
}

export default AboutTheAuthor; 